import CoffeeIcon from '@mui/icons-material/Coffee';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HubIcon from '@mui/icons-material/Hub';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WebIcon from '@mui/icons-material/Web';
import DnsIcon from '@mui/icons-material/Dns';
import BuildIcon from '@mui/icons-material/Build';
import { htmlLessons } from './html';
import { cssLessons } from './css';
import { reactLessons } from './react';
import { javascriptLessons } from './javascript';
import { nodeLessons } from './nodejs';
import { mongoLessons } from './mongodb';
import { javaLessons } from './java';
import { gitLessons } from './git';
import { dsaLessons } from './dsa';
import { agenticAILessons } from './fundamentals';
import { systemDesignLessons } from './systemDesign';

// Groups appear on the Learn page in this order. The featured group (DSA and
// System Design) is shown first and highlighted in the sidebar and overview.
export const learnGroups = ['CS Fundamentals', 'Frontend', 'Backend', 'Tools', 'AI'];
export const featuredGroup = { name: 'CS Fundamentals', label: 'Interview essentials' };

// How each group looks in the sidebar and overview: display label, icon and
// accent colours for its card.
export const groupStyles = {
  'CS Fundamentals': { label: featuredGroup.label, icon: AutoAwesomeIcon, accent: ['#22d3ee', '#a855f7'] },
  Frontend: { label: 'Frontend', icon: WebIcon, accent: ['#fb923c', '#facc15'] },
  Backend: { label: 'Backend', icon: DnsIcon, accent: ['#4ade80', '#14b8a6'] },
  Tools: { label: 'Tools', icon: BuildIcon, accent: ['#fb7185', '#f97316'] },
  AI: { label: 'AI', icon: SmartToyIcon, accent: ['#f472b6', '#a855f7'] }
};

// Each technology shows either a logo image or an MUI icon.
export const technologies = [
  {
    slug: 'html',
    name: 'HTML',
    group: 'Frontend',
    description: 'How pages work, semantics, accessibility, forms, media and SEO.',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/3/38/HTML5_Badge.svg',
    accent: ['#f97316', '#fb923c'],
    lessons: htmlLessons
  },
  {
    slug: 'css',
    name: 'CSS',
    group: 'Frontend',
    description: 'Box model, cascade, flexbox, grid, positioning, responsive design and motion.',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/6/62/CSS3_logo.svg',
    accent: ['#3b82f6', '#22d3ee'],
    lessons: cssLessons
  },
  {
    slug: 'javascript',
    name: 'JavaScript',
    group: 'Frontend',
    description: 'Scope, closures, this, prototypes, the event loop and async code.',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/9/99/Unofficial_JavaScript_logo_2.svg',
    accent: ['#facc15', '#f59e0b'],
    lessons: javascriptLessons
  },
  {
    slug: 'react',
    name: 'React',
    group: 'Frontend',
    description: 'Components, state, effects, lists, forms, context, hooks and performance.',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg',
    accent: ['#22d3ee', '#6366f1'],
    lessons: reactLessons
  },
  {
    slug: 'nodejs',
    name: 'Node.js',
    group: 'Backend',
    description: 'How Node works, Express APIs, middleware, authentication and production.',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/d/d9/Node.js_logo.svg',
    accent: ['#4ade80', '#16a34a'],
    lessons: nodeLessons
  },
  {
    slug: 'mongodb',
    name: 'MongoDB',
    group: 'Backend',
    description: 'CRUD, indexes, data modelling, aggregation and Mongoose.',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/9/93/MongoDB_Logo.svg',
    accent: ['#22c55e', '#14b8a6'],
    lessons: mongoLessons
  },
  {
    slug: 'java',
    name: 'Java',
    group: 'Backend',
    description: 'OOP, collections, exceptions, streams, concurrency, Spring Boot and JPA.',
    icon: CoffeeIcon,
    accent: ['#f97316', '#ef4444'],
    lessons: javaLessons
  },
  {
    slug: 'git',
    name: 'Git',
    group: 'Tools',
    description: 'Core workflow, branching and PRs, undoing mistakes and team workflows.',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/3/3f/Git_icon.svg',
    accent: ['#f43f5e', '#f97316'],
    lessons: gitLessons
  },
  {
    slug: 'dsa',
    name: 'Data Structures & Algorithms',
    group: 'CS Fundamentals',
    description: 'Big-O, hashing, two pointers, binary search, linked lists, trees, graphs and DP.',
    icon: AccountTreeIcon,
    accent: ['#4ade80', '#14b8a6'],
    lessons: dsaLessons
  },
  {
    slug: 'system-design',
    name: 'System Design',
    group: 'CS Fundamentals',
    description: 'A step-by-step interview method, core building blocks, and full case studies like chat, feeds and rate limiters.',
    icon: HubIcon,
    accent: ['#60a5fa', '#6366f1'],
    lessons: systemDesignLessons
  },
  {
    slug: 'agentic-ai',
    name: 'Agentic AI',
    group: 'AI',
    description: 'LLMs, tool use and retrieval-augmented generation.',
    icon: SmartToyIcon,
    accent: ['#f472b6', '#a855f7'],
    lessons: agenticAILessons
  }
];

export const getTechnology = (slug) => technologies.find((t) => t.slug === slug);

// Total interview Q&A blocks across every lesson, shown on the homepage.
export const interviewQuestionCount = technologies.reduce(
  (sum, t) => sum + t.lessons.reduce((n, l) => n + l.content.filter((b) => b.type === 'qa').length, 0),
  0
);
